import PropTypes from "prop-types";
import * as React from "react";
import useVideoRecorder from "../../hooks/useVideoRecorder";
import { ACCEPTED_VIDEO_TYPES } from "../../hooks/videoRecorderDefaults";
import {
  Alert,
  Button,
  Dialog,
  RestartIcon,
  StopIcon,
  UploadIcon,
  VideocamIcon,
} from "../../themeV2";
import CameraPreview from "../VideoRecorder/CameraPreview";
import CountdownOverlay from "../VideoRecorder/CountdownOverlay";
import RecordingTimer from "../VideoRecorder/RecordingTimer";
import { STATUS, uploadErrorCopy } from "../../domain/mediaUpload";
import { PHASE, actionsFor, viewPhase } from "./captureActions";
import MediaProgress from "./MediaProgress";
import PlaybackControls from "./PlaybackControls";
import s from "./MediaCaptureDialog.module.css";

const ICONS = {
  upload: UploadIcon,
  record: VideocamIcon,
  stop: StopIcon,
  restart: RestartIcon,
};

const iconFor = (name) => {
  const Icon = ICONS[name];
  return Icon ? <Icon /> : undefined;
};

const REVIEWING = new Set([PHASE.PREVIEW, PHASE.SENDING, PHASE.SETTLING]);

/* The one capture dialog. What differs between its flows is configuration, not
   control flow — the latter lives in captureActions. */
function MediaCaptureDialog({
  title,
  description,
  size = "md",
  initialBlob,
  recorderConfig,
  uploadState,
  uploadType,
  onSubmit,
  onClose,
  onCancelSend,
  onRetrySend,
  canUpload,
  canCancelSend,
  canCloseWhileSettling,
  submitLabel,
  confirmOnClose,
  showPlaybackControls,
  warning,
  children,
}) {
  const [confirmingClose, setConfirmingClose] = React.useState(false);

  const {
    state: recorderState,
    videoRef,
    fileInputRef,
    startCountdown,
    stopRecording,
    reset,
    retry,
    ensureStream,
    selectFile,
    handleFileSelect,
    getBlob,
    loadBlob,
    showMirror,
    timerDisplay,
  } = useVideoRecorder(recorderConfig);

  const phase = viewPhase(recorderState.phase, uploadState.status);
  const error =
    uploadState.status === STATUS.FAILED
      ? uploadErrorCopy({ ...uploadState, uploadType })
      : recorderState.error;
  const isReviewing = REVIEWING.has(phase);

  // Reopened to retry a take that failed to send: drop straight into preview
  // with the recording already in hand rather than asking for it again.
  const loadBlobRef = React.useRef(loadBlob);
  loadBlobRef.current = loadBlob;

  React.useEffect(() => {
    if (initialBlob) loadBlobRef.current(initialBlob);
  }, [initialBlob]);

  const requestClose = () => {
    if (confirmOnClose && phase !== PHASE.IDLE) {
      setConfirmingClose(true);
      return;
    }
    onClose();
  };

  const beginRecording = async () => {
    reset();

    if (!(await ensureStream())) return;

    startCountdown();
  };

  const handlers = {
    cancel: requestClose,
    close: onClose,
    selectFile,
    record: beginRecording,
    stop: phase === PHASE.COUNTDOWN ? reset : stopRecording,
    retry,
    submit: () => onSubmit(getBlob(), recorderState),
    cancelSend: onCancelSend,
    retrySend: onRetrySend,
  };

  const actions = actionsFor(phase, {
    canUpload,
    canCancelSend,
    canCloseWhileSettling,
    submitLabel,
    upload: uploadState,
  });

  return (
    <React.Fragment>
      <Dialog.Root
        open
        onOpenChange={(isOpen) => {
          if (!isOpen) requestClose();
        }}
      >
        <Dialog.Popup size={size}>
          <Dialog.Close />
          <Dialog.Title>{title}</Dialog.Title>

          <Dialog.Body className={s.body}>
            {description && (
              <Dialog.Description>{description}</Dialog.Description>
            )}

            {children}

            <CameraPreview
              ref={videoRef}
              mirror={showMirror}
              ariaLabel={
                isReviewing ? "Recorded video preview" : "Camera preview"
              }
            >
              {phase === PHASE.COUNTDOWN && (
                <CountdownOverlay value={recorderState.countdownValue} />
              )}
              {phase === PHASE.RECORDING && (
                <RecordingTimer display={timerDisplay} />
              )}
            </CameraPreview>

            {showPlaybackControls && phase === PHASE.PREVIEW && (
              <PlaybackControls videoRef={videoRef} />
            )}

            {(phase === PHASE.SENDING || phase === PHASE.SETTLING) && (
              <MediaProgress state={uploadState} />
            )}

            {warning && phase === PHASE.PREVIEW && (
              <Alert severity="warning">{warning}</Alert>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_VIDEO_TYPES}
              onChange={handleFileSelect}
              className={s.fileInput}
              aria-label="Upload video file"
            />
          </Dialog.Body>

          <Dialog.Actions>
            {actions.map(({ key, label, variant, icon }) => (
              <Button
                key={key}
                variant={variant}
                leading={iconFor(icon)}
                onClick={handlers[key]}
              >
                {label}
              </Button>
            ))}
          </Dialog.Actions>
        </Dialog.Popup>
      </Dialog.Root>

      {confirmingClose && (
        <Dialog.Root
          open
          alert
          onOpenChange={(isOpen) => {
            if (!isOpen) setConfirmingClose(false);
          }}
        >
          <Dialog.Popup size="sm">
            <Dialog.Title>Unsaved video</Dialog.Title>
            <Dialog.Body>
              You have not submitted your video yet. Are you sure you want to
              close? You will lose your video.
            </Dialog.Body>
            <Dialog.Actions>
              <Button variant="text" onClick={() => setConfirmingClose(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setConfirmingClose(false);
                  onClose();
                }}
              >
                Discard
              </Button>
            </Dialog.Actions>
          </Dialog.Popup>
        </Dialog.Root>
      )}
    </React.Fragment>
  );
}

MediaCaptureDialog.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.node,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  initialBlob: PropTypes.object,
  recorderConfig: PropTypes.object.isRequired,
  uploadState: PropTypes.object.isRequired,
  uploadType: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onCancelSend: PropTypes.func,
  onRetrySend: PropTypes.func,
  canUpload: PropTypes.bool,
  canCancelSend: PropTypes.bool,
  canCloseWhileSettling: PropTypes.bool,
  submitLabel: PropTypes.string,
  confirmOnClose: PropTypes.bool,
  showPlaybackControls: PropTypes.bool,
  warning: PropTypes.node,
  children: PropTypes.node,
};

export default MediaCaptureDialog;
